import { useCallback, useEffect, useState } from 'react';
import { useMatch, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import queryString from 'query-string';
import {
  Tabs,
  IconDownload,
  IconRefresh,
  ButtonPseudo,
  DateInputRange,
  ButtonSize,
  PermissionEnum,
  PermissionDenied,
  IconLoader,
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  ButtonSecondary,
  Checkbox,
  ResponseError,
  getResponseErrors,
  AccountingJob,
  AccountingJobProps,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useLazyGetBalanceSheetQuery,
  useExportBalanceSheetReportMutation,
  useExportBalanceSheetReportWithAccountsMutation,
} from './store/service';
import {
  useLazyGetAccountingJobsInProgressQuery,
  useLazyGetAccountingJobsInProgressStatesQuery,
} from '../../store/dictionaries/service';
import { BalanceSheet } from './types';
import { setNotificationVisible } from '../../../../../store/slice';
import { clearAccountingJobs } from '../../store/dictionaries/slice';

import ROUTES from '../../routes';

import cn from './balance.module.css';

let timeout: NodeJS.Timeout;

const Balance = () => {
  const match = useMatch(ROUTES.REPORTS_BALANCE);
  const sheetMatch = useMatch(ROUTES.REPORTS_BALANCE_SHEET);
  const navigate = useNavigate();
  const { search } = useLocation();
  const [downloadReportVisible, setDownloadReportVisible] = useState(false);
  const [downloadReportDates, setDownloadReportDates] = useState<Date[]>([]);
  const [type, setType] = useState('without');
  const [errorList, setErrorList] = useState('');
  const [excludeRevaluation, setExcludeRevaluation] = useState<boolean>(false);
  const [currentAccountingJob, setCurrentAccountingJob] =
    useState<AccountingJobProps>();
  const [precision, setPrecision] = useState('2');

  const [dates, setDates] = useState<Date[]>([]);

  const {
    reportsDictionaries: { activePermissions, accountingJobs },
    balance: {
      sheet: { formationTime, values, accountsCategories },
    },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const [getBalanceSheet, { isLoading, isFetching }] =
    useLazyGetBalanceSheetQuery();
  const [
    getAccountingJobs,
    {
      isLoading: accountingJobsIsLoading,
      isFetching: accountingJobsIsFetching,
    },
  ] = useLazyGetAccountingJobsInProgressQuery();
  const [getAccountingJobsStates] =
    useLazyGetAccountingJobsInProgressStatesQuery();

  const [
    exportReport,
    { isLoading: exportInProgress, isSuccess: exportIsSuccess },
  ] = useExportBalanceSheetReportMutation();

  const [
    exportReportWithAccounts,
    {
      isLoading: exportWithAccountsInProgress,
      isSuccess: exportWithAccountsIsSuccess,
    },
  ] = useExportBalanceSheetReportWithAccountsMutation();

  const getBalanceSheetAction = useCallback(async (searchValue: string) => {
    const response = (await getBalanceSheet(searchValue)) as unknown as {
      data?: BalanceSheet;
      error?: ResponseError;
      statusCode: number;
    };

    if (response?.error) {
      getResponseErrors({
        error: response.error,
        setErrorList,
      });
    }
  }, []);

  const onClearTimeout = async () => {
    await clearTimeout(timeout);
  };

  const onGetStates = async (query: string) => {
    await onClearTimeout();

    timeout = setTimeout(async () => {
      await getAccountingJobsStates(query);
      await onGetStates(query);
    }, 20000);
  };

  const onExportClick = async () => {
    const body: {
      createdAtFrom?: string;
      createdAtTo?: string;
      roundDecimals?: string;
    } = {};

    if (downloadReportDates && downloadReportDates.length) {
      body.createdAtFrom = new Date(downloadReportDates[0]).toISOString();

      if (downloadReportDates.length > 1) {
        body.createdAtTo = new Date(downloadReportDates[1]).toISOString();
      }
    }

    if (precision !== '2') {
      body.roundDecimals = precision;
    }

    if (type === 'without') {
      await exportReport(body);
    } else if (type === 'with') {
      await exportReportWithAccounts(body);
    }
  };

  useEffect(() => {
    if (exportIsSuccess || exportWithAccountsIsSuccess) {
      setDownloadReportVisible(false);
      setDownloadReportDates(dates);
      setType('without');

      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Report in progress. You'll be notified.",
          type: 'bs-report',
        }),
      );
    }
  }, [exportIsSuccess, exportWithAccountsIsSuccess]);

  useEffect(() => {
    if (
      activePermissions &&
      activePermissions?.includes(PermissionEnum.REPORTS_BALANCE_SHEET_LIST) &&
      !search
    ) {
      const date = new Date();

      date.setHours(0);
      date.setMinutes(0);
      date.setSeconds(0);
      date.setMilliseconds(0);

      setDates([new Date(date)]);
      setDownloadReportDates([new Date(date)]);

      navigate(
        `${match || sheetMatch ? 'sheet' : 'frs'}?transferDateFrom=${format(
          new Date(date),
          'yyyy-MM-dd',
        )}`,
      );
    }
  }, [activePermissions, search, match, sheetMatch]);

  useEffect(() => {
    if (
      accountingJobs &&
      accountingJobs?.length &&
      !accountingJobsIsFetching &&
      !accountingJobsIsLoading
    ) {
      let currentJob: AccountingJobProps | undefined;

      if (accountingJobs?.find((item) => item.state === 'IN_PROGRESS')) {
        currentJob = accountingJobs?.find(
          (item) => item.state === 'IN_PROGRESS',
        );
      } else if (
        !accountingJobs?.find((item) => item.state === 'IN_PROGRESS') &&
        accountingJobs?.find((item) => item.state === 'QUEUED')
      ) {
        currentJob = accountingJobs?.find((item) => item.state === 'QUEUED');
      } else if (
        !accountingJobs?.find((item) => item.state === 'IN_PROGRESS') &&
        !accountingJobs?.find((item) => item.state === 'QUEUED') &&
        accountingJobs?.find((item) => item.state === 'NEW')
      ) {
        currentJob = accountingJobs?.find((item) => item.state === 'NEW');
      }

      if (currentJob) {
        setCurrentAccountingJob(currentJob);
      }
    }
  }, [accountingJobs, accountingJobsIsFetching, accountingJobsIsLoading]);

  useEffect(() => {
    if (
      activePermissions &&
      activePermissions?.includes(PermissionEnum.REPORTS_BALANCE_SHEET_LIST) &&
      search &&
      accountsCategories
    ) {
      const parsed = queryString.parse(search);

      const parsedDates: Date[] = [];

      if (parsed?.transferDateFrom) {
        parsedDates.push(new Date(parsed?.transferDateFrom as string));
      }

      if (parsed.transferDateTo) {
        parsedDates.push(new Date(parsed?.transferDateTo as string));
      }

      if (parsed.excludeRevaluation) {
        setExcludeRevaluation(true);
      }

      if (parsed.roundDecimals) {
        setPrecision(parsed.roundDecimals as string);
      }

      setDates(parsedDates);
      setDownloadReportDates(parsedDates);

      getBalanceSheetAction(search);
    }
  }, [activePermissions, search, accountsCategories]);

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.REPORTS_ACCOUNTING_JOBS_LITS)
    ) {
      getAccountingJobs(
        '?type=BALANCES_LOGS&type=BALANCE_SHEET&type=REVALUATION&state=IN_PROGRESS&state=QUEUED&state=NEW&all=true',
      );

      onGetStates(
        '?type=BALANCES_LOGS&type=BALANCE_SHEET&type=REVALUATION&state=IN_PROGRESS&state=QUEUED&state=NEW&all=true',
      );
    }
  }, [activePermissions]);

  useEffect(
    () => () => {
      dispatch(clearAccountingJobs());

      setTimeout(async () => {
        await onClearTimeout();
      });
    },
    [],
  );

  let content = (
    <div className={cn.balanceReportLoader}>
      <IconLoader />
    </div>
  );

  useEffect(() => {
    let title = 'Balance report';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  if (activePermissions) {
    if (
      !activePermissions.includes(PermissionEnum.REPORTS_BALANCE_SHEET_LIST) ||
      !activePermissions.includes(PermissionEnum.SECTION_REPORTS)
    ) {
      content = <PermissionDenied />;
    } else {
      content = (
        <div className={cn.balance}>
          <div className={cn.balanceTop}>
            <div className={cn.balanceTitle}>Balance sheet</div>
            <div className={cn.balanceTopControls}>
              <div className={cn.balanceTopControlsLeft}>
                <Tabs
                  wrapperClassName={cn.balanceTopTabs}
                  tabs={[
                    {
                      name: 'Balance sheet',
                      path: `${ROUTES.REPORTS_BALANCE_SHEET}${search || ''}`,
                    },
                    {
                      name: 'FRS 102',
                      path: `${ROUTES.REPORTS_BALANCE_FRS}${search || ''}`,
                    },
                  ]}
                  enableRouting
                />
                <DateInputRange
                  className={cn.balanceDateInput}
                  placeholder="All time"
                  values={dates.map((item) => item.toISOString())}
                  onReset={() => {
                    let link = sheetMatch ? 'sheet' : 'frs';

                    if (excludeRevaluation) {
                      link = `${link}?excludeRevaluation=${excludeRevaluation}`;
                    }

                    if (precision !== '2') {
                      link = `${link}&roundDecimals=${precision}`;
                    }

                    navigate(link);

                    setDates([]);
                    setDownloadReportDates([]);
                  }}
                  onChangeRange={(dateValues) => {
                    setDates(dateValues);
                    setDownloadReportDates(dateValues);

                    let link = `?transferDateFrom=${format(
                      new Date(dateValues[0]),
                      'yyyy-MM-dd',
                    )}`;

                    if (dateValues.length > 1) {
                      link = `${link}&transferDateTo=${format(
                        new Date(dateValues[1]),
                        'yyyy-MM-dd',
                      )}`;
                    }

                    if (excludeRevaluation) {
                      link = `${link}&excludeRevaluation=${excludeRevaluation}`;
                    }

                    if (precision !== '2') {
                      link = `${link}&roundDecimals=${precision}`;
                    }

                    navigate(link);
                  }}
                  withoutCustomSelector
                />
              </div>
              <div className={cn.balanceTopControlsRight}>
                <ButtonPseudo
                  text="Refresh"
                  icon={
                    <IconRefresh
                      className={isFetching && values ? cn.refreshing : ''}
                    />
                  }
                  className={`button-white ${cn.balanceRefreshButton}`}
                  size={ButtonSize.SMALL}
                  disabled={isLoading || isFetching}
                  onClick={() => {
                    getBalanceSheetAction(search);
                    setErrorList('');
                  }}
                />
                {activePermissions?.includes(
                  PermissionEnum.REPORTS_BALANCE_SHEET_EXPORT_ACTION,
                ) && (
                  <ButtonPseudo
                    text="Download Report"
                    icon={<IconDownload />}
                    className="button-white"
                    size={ButtonSize.SMALL}
                    onClick={() => setDownloadReportVisible(true)}
                    disabled={Boolean(errorList)}
                  />
                )}
              </div>
            </div>
            <div className={cn.balanceTopSearchInfo}>
              {formationTime?.end
                ? `Formed in ${formationTime?.duration} sec: ${new Date(
                    formationTime?.start || '',
                  ).toLocaleDateString('en-US', {
                    month: 'short',
                    year: 'numeric',
                    day: 'numeric',
                  })} from ${new Date(formationTime?.start || '')
                    .toLocaleString()
                    .slice(-8)} to ${new Date(formationTime?.end)
                    .toLocaleString()
                    .slice(-8)}`
                : 'Forming...'}
            </div>
            {activePermissions?.includes(
              PermissionEnum.REPORTS_ACCOUNTING_JOBS_LITS,
            ) && (
              <AccountingJob
                accountingJobsIsLoading={
                  accountingJobsIsLoading || accountingJobsIsFetching
                }
                accountingJobs={accountingJobs}
                accountingJob={currentAccountingJob}
                jobsLength={accountingJobs?.length}
                query="?type=BALANCES_LOGS&type=BALANCE_SHEET&type=REVALUATION&state=IN_PROGRESS&state=QUEUED&state=NEW"
              />
            )}
          </div>
          <Outlet
            context={{
              isLoading: isLoading || isFetching,
              precision,
              setPrecision,
              errorList,
              dates,
              excludeRevaluation,
              setExcludeRevaluation,
            }}
          />
          <SidePanel
            title="Download report"
            isOpen={downloadReportVisible}
            onClose={() => {
              setDownloadReportVisible(false);
              setDownloadReportDates(dates);
              setType('without');
            }}
            sidepanelExpandedKey="download-report-panel"
            type={SidePanelType.AML}
            footer={
              <div className={cn.statementPanelControls}>
                <ButtonPrimary
                  text={
                    exportInProgress || exportWithAccountsInProgress
                      ? 'Loading...'
                      : 'Download'
                  }
                  icon={<IconDownload />}
                  disabled={exportInProgress || exportWithAccountsInProgress}
                  onClick={onExportClick}
                  bold
                />
                <ButtonSecondary
                  className="button-white"
                  text="Close"
                  onClick={() => {
                    setDownloadReportDates(dates);
                    setDownloadReportVisible(false);
                    setType('without');
                  }}
                  bold
                />
              </div>
            }
            closeOnClickOutside
          >
            <>
              <div className={cn.exportTypeTitle}>Export type</div>
              <div className={cn.radioWrapper}>
                <Checkbox
                  name="type"
                  type="radio"
                  value="without"
                  title="Without accounts"
                  checked={type === 'without'}
                  onChange={() => setType('without')}
                />
              </div>
              <div className={cn.radioWrapper}>
                <Checkbox
                  name="type"
                  type="radio"
                  value="with"
                  title="With accounts"
                  checked={type === 'with'}
                  onChange={() => setType('with')}
                />
              </div>
              <div
                className={`${cn.datesWrapper} ${
                  !downloadReportDates.length ? cn.empty : ''
                }`}
              >
                <div className={cn.dateInputTitle}>Report period</div>
                <DateInputRange
                  values={downloadReportDates?.map((item) =>
                    item.toISOString(),
                  )}
                  onChangeRange={(date) => setDownloadReportDates(date)}
                  placeholder="Select date"
                  withoutTwoPickers
                  withoutCustomSelector
                />
              </div>
            </>
          </SidePanel>
        </div>
      );
    }
  }

  return content;
};

export default Balance;
